import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";

import LeftbarTitle from "../../../components/LeftbarTitle";
import ButtonSpinner from "../../../components/ButtonSpinner";

interface ConfirmProps {
    show: boolean;
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    danger?: boolean;
    onConfirm: () => Promise<unknown> | void;
    onClose: () => void;
}

/**
 * A confirmation in the app's modal style (components/RemoveForm), for what window.confirm did.
 * The confirm button spins until onConfirm's promise settles.
 */
export const ConfirmModal = ({ show, title, message, confirmLabel, danger = false, onConfirm, onClose }: ConfirmProps) => {
    const { t } = useTranslation(['common']);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (!show) setBusy(false); }, [show]);

    const confirm = () => {
        setBusy(true);
        Promise.resolve(onConfirm()).finally(() => setBusy(false));
    };

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton><LeftbarTitle title={title} /></Modal.Header>
            <Modal.Body>{message}</Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onClose}>{t('common:cancel')}</Button>
                <Button variant={danger ? 'danger' : 'primary'} disabled={busy} onClick={confirm}>
                    {confirmLabel || t('common:confirm')}{busy && <ButtonSpinner />}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

interface NameProps {
    show: boolean;
    title: string;
    label: string;
    initial?: string;
    onSave: (name: string) => Promise<unknown> | void;
    onClose: () => void;
}

/** A one-field name form in the app's modal style, for what window.prompt did. */
export const NameModal = ({ show, title, label, initial = '', onSave, onClose }: NameProps) => {
    const { t } = useTranslation(['common']);
    const [name, setName] = useState(initial);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (show) { setName(initial); setBusy(false); } }, [show, initial]);

    const save = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!name.trim()) {
            return;
        }
        setBusy(true);
        Promise.resolve(onSave(name.trim())).finally(() => setBusy(false));
    };

    return (
        <Modal show={show} onHide={onClose}>
            <form onSubmit={save}>
                <Modal.Header closeButton><LeftbarTitle title={title} /></Modal.Header>
                <Modal.Body>
                    <label className="form-label" htmlFor="wf-name-input">{label}</label>
                    <input id="wf-name-input" className="form-control" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={onClose}>{t('common:cancel')}</Button>
                    <Button variant="primary" type="submit" disabled={busy || !name.trim()}>
                        {t('common:save')}{busy && <ButtonSpinner />}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};
